/**
 * The bills still sitting on the floor, and settling one.
 *
 * WHY THIS EXISTS
 * ---------------
 * A waiter takes an order at the table on their phone and cannot take money —
 * there is no payment screen in their app, and there should not be. So the
 * bill has to be settled by whoever is on the till, and until now there was
 * nothing anywhere that could do it: money could only enter the system at the
 * moment an order was created. A table ordered by a waiter stayed unpaid for
 * ever, which meant it stayed occupied for ever.
 *
 * WHY IT IS A LIST OF TABLES AND NOT A LIST OF ORDERS
 * --------------------------------------------------
 * Because that is what the customer says. Nobody walks to the counter and asks
 * to settle FR-FR001-000014; they say "table four". The order number is shown
 * underneath for the till roll, but the thing you tap is the table.
 *
 * WHY "READY" IS MARKED
 * ---------------------
 * The same status the waiter's phone shows. A cashier glancing at this list
 * can see which tables are still waiting on the kitchen, which is the question
 * they get asked while somebody stands at the counter.
 */
import { useQuery } from '@tanstack/react-query';

import { api } from '../../lib/api.js';
import { formatPKR } from '@foodrun/shared';
import { Button } from '../../components/ui/Button.jsx';
import { Spinner } from '../../components/ui/Spinner.jsx';

/**
 * How the kitchen's progress reads to somebody on the till.
 *
 * 'Served' is not in this list because it is not a status. The kitchen sets
 * `completed` when a ticket leaves the pass — that is their screen being
 * cleared, not the food reaching a table. Only the waiter can say that, and
 * they say it by setting `servedAt`.
 */
const PROGRESS = {
  confirmed: { label: 'With the kitchen', tone: 'text-ink-3' },
  preparing: { label: 'Being cooked', tone: 'text-ink-3' },
  ready: { label: 'Ready to serve', tone: 'text-brand-300' },
  packed: { label: 'Ready to serve', tone: 'text-brand-300' },
  completed: { label: 'Ready to serve', tone: 'text-brand-300' },
};

/** What a table reads, given what the kitchen and the waiter have each done. */
function progressFor(order) {
  if (order.servedAt) return { label: 'Served', tone: 'text-ink-3' };
  return PROGRESS[order.status] ?? { label: order.status, tone: 'text-ink-3' };
}

export function OpenTablesSheet({ onSettle, onClose, settling }) {
  const { data, isLoading } = useQuery({
    queryKey: ['floor'],
    queryFn: () => api.get('/pos/tables'),
    // Somebody is standing at the counter; a stale total is a wrong total.
    refetchInterval: 10_000,
  });

  const taken = (data?.floor ?? []).filter((table) => table.occupied);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Tables with a bill to settle"
      className="absolute inset-0 z-30 flex items-end justify-center bg-black/50 p-3 sm:items-center"
    >
      <div className="w-full max-w-md rounded-2xl bg-surface p-5 shadow-xl">
        <div className="mb-4 flex items-baseline justify-between">
          <h2 className="font-semibold text-ink text-lg">Tables to settle</h2>
          <button
            onClick={onClose}
            className="rounded-lg px-2.5 py-1.5 text-ink-2 text-sm hover:bg-surface-3"
          >
            Close
          </button>
        </div>

        {isLoading ? (
          <div className="py-8">
            <Spinner label="Looking at the floor" />
          </div>
        ) : taken.length === 0 ? (
          <p className="py-8 text-center text-ink-3 text-sm">
            Nothing is sitting unpaid. Every table is free.
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {taken.map((table) => {
              const progress = progressFor(table.order);

              return (
                <li
                  key={table.id}
                  className="flex items-center gap-3 rounded-xl bg-surface-2 p-3"
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-ink">
                      Table {table.number}
                      <span className={`ml-2 font-normal text-xs ${progress.tone}`}>
                        {progress.label}
                      </span>
                    </p>
                    <p className="truncate text-ink-3 text-xs">
                      {table.order.number}
                      {table.order.servedBy ? ` · ${table.order.servedBy}` : ''}
                    </p>
                  </div>

                  <p className="font-semibold text-ink tabular-nums">
                    {formatPKR(table.order.total)}
                  </p>

                  <Button
                    size="sm"
                    busy={settling === table.order.id}
                    onClick={() => onSettle(table.order)}
                  >
                    Take payment
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
